import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  getProofreadingBatch,
  getProofreadingResume,
  type AiProofreadingBatchStatus,
  type AiProofreadingResultPayload,
  type ProofreadingBatchCheckOptions,
  type ResolvedProofreadingBatchChecks,
} from "@/lib/api/proofreadingAi";
import {
  clearStoredAiProofreadingBatch,
  loadStoredAiProofreadingBatch,
  saveStoredAiProofreadingBatch,
} from "@/lib/utils/proofreadingAiBatchStorage";
import {
  calcAiProofreadingFishEstimate,
  countEnabledAiProofreadingChecks,
  resolveAiProofreadingChecks,
} from "@/lib/utils/proofreadingAiFish";

const STORAGE_KEY = "proofreadingAi";

export type ProofreadingMode = "basic" | "ai";

type PersistedAiState = {
  mode: ProofreadingMode;
  /** 選択原稿・文字数が属する作品 ID。別作品へ引き継がないためのスコープ。 */
  activeNovelId: string | null;
  selectedManuscriptKeys: string[];
  totalChars: number;
  selectedChecks: ResolvedProofreadingBatchChecks;
  estimatedFish: number;
  manuscriptCharCounts: Record<string, number>;
  aiResult: AiProofreadingResultPayload | null;
};

function loadPersistedState(): PersistedAiState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedAiState;
  } catch {
    return null;
  }
}

function savePersistedState(state: PersistedAiState): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // QuotaExceededError 等。aiResult は種別あたり最大 150 件 × 3 で肥大しうる。
    // 保存に失敗してもメモリ上の結果は残る。タブ再読込後は
    // GET /proofreading/resume/:novelId で COMPLETED を検知し、
    // 待ち画面の finish API から result を取り直す。
  }
}

const defaultChecks = resolveAiProofreadingChecks();

/**
 * AI校正ウィザード用 Pinia ストア。
 * 基本校正は `useProofreadingBasicStore` と分離する。
 */
export const useProofreadingAiStore = defineStore("proofreadingAi", () => {
  const persisted = loadPersistedState();

  const mode = ref<ProofreadingMode>(persisted?.mode ?? "basic");
  const activeNovelId = ref<string | null>(persisted?.activeNovelId ?? null);
  const selectedManuscriptKeys = ref<string[]>(persisted?.selectedManuscriptKeys ?? []);
  const totalChars = ref<number>(persisted?.totalChars ?? 0);
  const selectedChecks = ref<ResolvedProofreadingBatchChecks>(
    resolveAiProofreadingChecks(persisted?.selectedChecks),
  );
  const estimatedFish = ref<number>(persisted?.estimatedFish ?? 0);
  const manuscriptCharCounts = ref<Record<string, number>>(persisted?.manuscriptCharCounts ?? {});
  const batchId = ref<string | null>(null);
  const batchStatus = ref<AiProofreadingBatchStatus | null>(null);
  // sessionStorage に aiResult が残らなくても、COMPLETED は resume API → finish で復元する。
  const aiResult = ref<AiProofreadingResultPayload | null>(persisted?.aiResult ?? null);

  const enabledCheckCount = computed(() => countEnabledAiProofreadingChecks(selectedChecks.value));

  const isAiMode = computed(() => mode.value === "ai");

  function hasAiReviseResult(novelId: string): boolean {
    return aiResult.value != null && aiResult.value.novelId === novelId;
  }

  function clearAiReviseResultForNovel(novelId: string): void {
    if (!hasAiReviseResult(novelId)) return;
    aiResult.value = null;
    persistWizardState();
  }

  function enterBasicMode(novelId: string): void {
    clearAiReviseResultForNovel(novelId);
    // 結果を破棄した以上、復元用のバッチ追跡も残さない（AI 画面へ差し戻らないように）。
    clearBatchTracking(novelId);
    setActiveNovelId(novelId);
    setMode("basic");
  }

  function enterAiMode(novelId: string): void {
    setActiveNovelId(novelId);
    setMode("ai");
  }

  function persistWizardState(): void {
    savePersistedState({
      mode: mode.value,
      activeNovelId: activeNovelId.value,
      selectedManuscriptKeys: selectedManuscriptKeys.value,
      totalChars: totalChars.value,
      selectedChecks: selectedChecks.value,
      estimatedFish: estimatedFish.value,
      manuscriptCharCounts: manuscriptCharCounts.value,
      aiResult: aiResult.value,
    });
  }

  function setMode(nextMode: ProofreadingMode): void {
    mode.value = nextMode;
    persistWizardState();
  }

  /** 選択原稿・文字数など作品に紐づく値だけを初期化する（aiResult は novelId で判定するため触らない）。 */
  function clearNovelScopedSelections(): void {
    selectedManuscriptKeys.value = [];
    totalChars.value = 0;
    estimatedFish.value = 0;
    manuscriptCharCounts.value = {};
  }

  function setActiveNovelId(novelId: string): void {
    if (!novelId || activeNovelId.value === novelId) return;
    // 別作品の選択原稿が残ったままだと、修正反映画面で原稿が 0 件になったり
    // Fish 見積りを取り違えたりする。作品が変わった時点で捨てる。
    if (activeNovelId.value != null) {
      clearNovelScopedSelections();
    }
    activeNovelId.value = novelId;
    persistWizardState();
  }

  function setManuscriptCharCounts(counts: Record<string, number>): void {
    manuscriptCharCounts.value = { ...counts };
    persistWizardState();
  }

  function setSelectedManuscriptKeys(keys: string[]): void {
    selectedManuscriptKeys.value = [...keys];
    persistWizardState();
  }

  function setTotalChars(chars: number): void {
    totalChars.value = Math.max(0, chars);
    estimatedFish.value = calcAiProofreadingFishEstimate(
      totalChars.value,
      enabledCheckCount.value,
    );
    persistWizardState();
  }

  function setSelectedChecks(checks: ProofreadingBatchCheckOptions): void {
    selectedChecks.value = resolveAiProofreadingChecks(checks);
    estimatedFish.value = calcAiProofreadingFishEstimate(
      totalChars.value,
      countEnabledAiProofreadingChecks(selectedChecks.value),
    );
    persistWizardState();
  }

  function setEstimatedFish(fish: number): void {
    estimatedFish.value = Math.max(0, fish);
    persistWizardState();
  }

  function rememberBatchId(novelId: string, nextBatchId: string): void {
    batchId.value = nextBatchId;
    saveStoredAiProofreadingBatch(novelId, nextBatchId);
  }

  function loadBatchIdFromStorage(novelId: string): string | null {
    const stored = loadStoredAiProofreadingBatch(novelId);
    batchId.value = stored?.batchId ?? null;
    return batchId.value;
  }

  function clearBatchTracking(novelId: string): void {
    batchId.value = null;
    batchStatus.value = null;
    clearStoredAiProofreadingBatch(novelId);
  }

  function setBatchStatus(status: AiProofreadingBatchStatus | null): void {
    batchStatus.value = status;
  }

  function setAiResult(result: AiProofreadingResultPayload | null): void {
    aiResult.value = result;
    if (result != null) {
      // 完了結果のレビュー中に画面を離脱・再読込しても、finish API から再取得できるよう保持する。
      rememberBatchId(result.novelId, result.batchId);
    }
    persistWizardState();
  }

  function applyResolvedBatchStatus(
    novelId: string,
    nextBatchId: string,
    nextStatus: AiProofreadingBatchStatus,
  ): AiProofreadingBatchStatus | null {
    batchStatus.value = nextStatus;
    if (nextStatus === "FAILED" || nextStatus === "CANCELLED") {
      clearBatchTracking(novelId);
      return null;
    }
    rememberBatchId(novelId, nextBatchId);
    return nextStatus;
  }

  /**
   * 再開対象の AI バッチ状態を返す。
   * 正本は `GET /proofreading/resume/:novelId`（userId + 作品ID の Dynamo Query）。
   * 失敗時のみ localStorage の batchId で個別 GET にフォールバックする。
   */
  async function resolveStoredBatchStatus(novelId: string): Promise<AiProofreadingBatchStatus | null> {
    try {
      const resume = await getProofreadingResume(novelId);
      if (resume.aiBatch) {
        return applyResolvedBatchStatus(
          novelId,
          resume.aiBatch.batchId,
          resume.aiBatch.batchStatus,
        );
      }
      batchStatus.value = null;
      return null;
    } catch {
      const storedBatchId = loadBatchIdFromStorage(novelId);
      if (!storedBatchId) {
        batchStatus.value = null;
        return null;
      }

      const response = await getProofreadingBatch(novelId, storedBatchId);
      return applyResolvedBatchStatus(novelId, storedBatchId, response.batchStatus);
    }
  }

  function resetWizardSelections(): void {
    clearNovelScopedSelections();
    selectedChecks.value = { ...defaultChecks };
    aiResult.value = null;
    persistWizardState();
  }

  /**
   * 原稿反映完了後など、AI 校正セッションを終了する。
   * aiResult / batch 追跡 / ウィザード選択状態をクリアする。
   */
  function clearAiReviseSession(novelId: string): void {
    aiResult.value = null;
    clearBatchTracking(novelId);
    resetWizardSelections();
    setMode("basic");
  }

  return {
    mode,
    activeNovelId,
    selectedManuscriptKeys,
    totalChars,
    selectedChecks,
    estimatedFish,
    manuscriptCharCounts,
    batchId,
    batchStatus,
    aiResult,
    enabledCheckCount,
    isAiMode,
    hasAiReviseResult,
    clearAiReviseResultForNovel,
    enterBasicMode,
    enterAiMode,
    setMode,
    setActiveNovelId,
    setSelectedManuscriptKeys,
    setManuscriptCharCounts,
    setTotalChars,
    setSelectedChecks,
    setEstimatedFish,
    rememberBatchId,
    loadBatchIdFromStorage,
    clearBatchTracking,
    setBatchStatus,
    setAiResult,
    resolveStoredBatchStatus,
    resetWizardSelections,
    clearAiReviseSession,
  };
});

export type ProofreadingAiStore = ReturnType<typeof useProofreadingAiStore>;
